import React from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useI18n } from "../../i18n";
import "./LandingPageV2.css";
import "./TemplateDetailPage.css";
import { TEMPLATE_DETAIL_CONTENT } from "./templateDetailsContent";

function pickLocalized(value, locale) {
  if (typeof value === "string") return value;
  if (!value) return "";
  return value[locale] || value.en || value.ru || "";
}

function splitLeadingIcon(text) {
  if (!text) return { icon: "", label: "" };
  const match = text.match(/^(\S+)\s+(.*)$/);
  if (!match) return { icon: "", label: text };
  return { icon: match[1], label: match[2] };
}

export default function TemplateDetailPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { locale, setLocale } = useI18n();

  const content = TEMPLATE_DETAIL_CONTENT[templateId];

  if (!content) {
    return <Navigate to="/templates" replace />;
  }

  const handleLocaleToggle = () => {
    setLocale(locale === "ru" ? "en" : "ru");
  };

  const handleSignUp = () => {
    try {
      localStorage.setItem("auth_mode", "signup");
      localStorage.setItem("template_interest", templateId);
    } catch (e) {}
    navigate(`/login?mode=signup&template=${templateId}`);
  };

  const handleSignIn = () => {
    try {
      localStorage.setItem("auth_mode", "login");
    } catch (e) {}
    navigate("/login");
  };

  const ui = {
    home: locale === "ru" ? "Главная" : "Home",
    templates: locale === "ru" ? "Шаблоны" : "Templates",
    otherTemplates: locale === "ru" ? "другие шаблоны" : "other templates",
    tryTemplate:
      locale === "ru" ? "🔮 Попробовать шаблон бесплатно" : "🔮 Try this template for free",
    footerCopyright:
      locale === "ru" ? "© 2025 Supchats · Защищено GDPR" : "© 2025 Supchats · GDPR protected",
    support: "support@supchats.com",
    backToTemplates:
      locale === "ru" ? "← Смотреть все шаблоны" : "← View all templates",
    signUp: locale === "ru" ? "Регистрация" : "Sign Up",
    signIn: locale === "ru" ? "Войти" : "Sign In",
  };

  const problemTitle = splitLeadingIcon(pickLocalized(content.problemTitle, locale));
  const solutionTitle = splitLeadingIcon(pickLocalized(content.solutionTitle, locale));

  return (
    <div className="template-detail-page">
      <header className="landing-v2-header">
        <div className="header-container">
          <Link to="/" className="logo">
            <img
              src={process.env.PUBLIC_URL + "/logo512.png"}
              alt="Supchats logo"
              className="logo-img"
            />
            <span className="logo-text">Supchats</span>
          </Link>
          <nav className="nav-menu">
            <div className="language-switcher">
              <label className="language-toggle-label">
                <input
                  type="checkbox"
                  checked={locale === "ru"}
                  onChange={handleLocaleToggle}
                  className="language-toggle-checkbox"
                />
                <span className="language-toggle-slider">
                  <span className="language-toggle-text">{locale === "ru" ? "RU" : "EN"}</span>
                </span>
              </label>
            </div>
            <Link to="/templates" className="nav-link-templates">
              {ui.templates}
            </Link>
            <button type="button" className="btn-signup" onClick={handleSignUp}>
              {ui.signUp}
            </button>
            <button type="button" className="btn-signin" onClick={handleSignIn}>
              {ui.signIn}
            </button>
          </nav>
        </div>
      </header>

      <main className="template-detail-main">
        <div className="template-detail-container">
          <div className="template-detail-breadcrumb">
            <Link to="/">{ui.home}</Link>
            <span>›</span>
            <Link to="/templates">{ui.templates}</Link>
            <span>›</span>
            <span className="current">{pickLocalized(content.badge, locale).replace(/^[^\s]+\s/, "")}</span>
          </div>

          <section className="template-detail-hero">
            <div className="template-detail-badge">{pickLocalized(content.badge, locale)}</div>
            <h1 className="template-detail-title">
              {pickLocalized(content.title, locale)
                .split("\n")
                .map((line, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
            </h1>
            <p className="template-detail-description">
              {pickLocalized(content.description, locale)}
            </p>
            <div className="template-detail-stats">{pickLocalized(content.stats, locale)}</div>
            <div className="template-detail-actions">
              <button
                type="button"
                className="template-detail-btn template-detail-btn-primary"
                onClick={handleSignUp}
              >
                {ui.tryTemplate}
              </button>
              <span className="template-detail-actions-note">
                · {locale === "ru" ? "или" : "or"}{" "}
                <Link to="/templates">{ui.otherTemplates}</Link>
              </span>
            </div>
          </section>

          <section className="template-detail-section">
            <h2 className="template-detail-section-title">
              {pickLocalized(content.audienceTitle, locale)}
            </h2>
            <p className="template-detail-section-description">
              {pickLocalized(content.audienceDescription, locale)}
            </p>
          </section>

          <section className="template-detail-features">
            {content.features.map((feature) => (
              <div className="template-detail-feature" key={pickLocalized(feature.title, locale)}>
                <div className="template-detail-feature-emoji">{feature.emoji}</div>
                <div>
                  <h3>{pickLocalized(feature.title, locale)}</h3>
                  <p>{pickLocalized(feature.description, locale)}</p>
                </div>
              </div>
            ))}
          </section>

          <section className="template-detail-problem-solution">
            <div className="template-detail-problem">
              <h3 className="template-detail-section-heading">
                {problemTitle.icon && (
                  <span className="template-detail-section-heading-icon">{problemTitle.icon}</span>
                )}
                <span>{problemTitle.label}</span>
              </h3>
              <ul>
                {content.problems.map((item, idx) => (
                  <li key={idx}>{pickLocalized(item, locale)}</li>
                ))}
              </ul>
            </div>
            <div className="template-detail-solution">
              <h3 className="template-detail-section-heading">
                {solutionTitle.icon && (
                  <span className="template-detail-section-heading-icon">{solutionTitle.icon}</span>
                )}
                <span className="template-detail-solution-heading-text">{solutionTitle.label}</span>
              </h3>
              <ul>
                {content.solutions.map((item, idx) => (
                  <li key={idx}>{pickLocalized(item, locale)}</li>
                ))}
              </ul>
            </div>
          </section>

          <section className="template-detail-section">
            <h2 className="template-detail-section-title">
              {pickLocalized(content.comparisonTitle, locale)}
            </h2>
            <div className="template-detail-table-wrap">
              <table className="template-detail-table">
                <thead>
                  <tr>
                    <th>{pickLocalized(content.comparisonHeaders.feature, locale)}</th>
                    <th>{pickLocalized(content.comparisonHeaders.ai, locale)}</th>
                    <th>{pickLocalized(content.comparisonHeaders.other, locale)}</th>
                  </tr>
                </thead>
                <tbody>
                  {content.comparisonRows.map((row, idx) => (
                    <tr key={idx}>
                      <td>{pickLocalized(row.feature, locale)}</td>
                      <td>{pickLocalized(row.ai, locale)}</td>
                      <td>{pickLocalized(row.other, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="template-detail-section">
            <h2 className="template-detail-section-title">
              {pickLocalized(content.stepsTitle, locale)}
            </h2>
            <div className="template-detail-steps">
              {content.steps.map((step, idx) => (
                <div className="template-detail-step" key={idx}>
                  <h4>{pickLocalized(step.title, locale)}</h4>
                  <p>{pickLocalized(step.description, locale)}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="template-detail-testimonial">
            <p>{pickLocalized(content.testimonial.quote, locale)}</p>
            <div className="template-detail-testimonial-author">
              {pickLocalized(content.testimonial.author, locale)}
            </div>
          </section>

          <section className="template-detail-cta">
            <h2>{pickLocalized(content.ctaTitle, locale)}</h2>
            <p>{pickLocalized(content.ctaDescription, locale)}</p>
            <button
              type="button"
              className="template-detail-btn template-detail-btn-primary template-detail-btn-large"
              onClick={handleSignUp}
            >
              {ui.tryTemplate}
            </button>
            <p className="template-detail-back-line">
              <Link to="/templates">{ui.backToTemplates}</Link>
            </p>
          </section>

          <footer className="template-detail-footer">
            <span>{ui.footerCopyright}</span>
            <span className="template-detail-footer-links">
              <a href={`mailto:${ui.support}`}>{ui.support}</a>
            </span>
          </footer>
        </div>
      </main>
    </div>
  );
}

